// @ts-nocheck
// @ts-ignore
import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { getViewParameterValue, ResizeNotifier, yieldToMainThread } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {string | undefined} */
  #pendingSelectedOptionValueId;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deferredVariantChangeTimeout;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      // Support cases where no variant is preselected
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // Check for variant parameter in URL and auto-select it
    // Use a small delay to ensure product form is connected
    setTimeout(() => {
      this.#selectVariantFromUrl();
      // If no variant was selected from URL, select the first available variant
      if (!this.#hasSelectedVariant()) {
        this.#selectFirstAvailableVariant();
      }
    }, 100);
  }

  /**
   * Updates the product form directly with a variant ID
   * This is used when selecting from URL to immediately enable the button
   */
  #updateProductFormFromVariantId(variantId) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const productForm = section.querySelector('product-form-component');
    if (!productForm) {
      return;
    }

    const variantIdInput = productForm.querySelector('input[name="id"][ref="variantId"]');
    if (variantIdInput) {
      variantIdInput.value = variantId;
    }

    // Check if variant is available before enabling button
    const matchingInput = this.querySelector(`input[data-variant-id="${variantId}"]`);
    const matchingOption = this.querySelector(`select option[data-variant-id="${variantId}"]`);
    const isAvailable = matchingInput?.dataset.optionAvailable !== 'false' &&
      matchingOption?.dataset.optionAvailable !== 'false';

    // Enable the add to cart button if variant is available
    const addToCartContainer = productForm.querySelector('add-to-cart-component');
    if (addToCartContainer && addToCartContainer.enable) {
      if (isAvailable) {
        addToCartContainer.enable();
      } else {
        addToCartContainer.disable();
      }
    }

    // Hide variant error message if present
    const variantError = section.querySelector('[data-variant-error]');
    if (variantError) {
      variantError.classList.add('hidden');
    }
  }

  /**
   * Checks if any variant is currently selected
   * @returns {boolean} True if a variant is selected
   */
  #hasSelectedVariant() {
    const variantIdInput = this.#getVariantIdInput();
    return variantIdInput && variantIdInput.value !== '';
  }

  /**
   * Gets the variant ID input from the product form
   * @returns {HTMLInputElement | null}
   */
  #getVariantIdInput() {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const productForm = section.querySelector('product-form-component');
    if (!productForm) return null;

    return productForm.querySelector('input[name="id"][ref="variantId"]');
  }

  /**
 * Updates URL with variant parameter
 */
  #updateUrlWithVariant(variantId) {
    if (!variantId) return;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);

    history.replaceState({}, '', url.toString());
  }

  /**
   * Selects the first available variant
   */
  #selectFirstAvailableVariant() {
    // Only auto-select if we're on a product page
    const isOnProductPage = this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isOnProductPage) {
      return;
    }

    // Try to get the first available variant from the JSON script tag
    const variantScript = this.querySelector('script[type="application/json"]');
    if (!variantScript) return;

    let firstVariant;
    try {
      firstVariant = JSON.parse(variantScript.textContent);
    } catch (e) {
      console.warn('[VariantPicker] Could not parse variant JSON:', e);
      return;
    }

    if (!firstVariant || !firstVariant.id) return;

    const variantId = String(firstVariant.id);

    // Find all inputs that match this variant ID
    const matchingInputs = Array.from(this.querySelectorAll(`input[data-variant-id="${variantId}"]`))
      .filter(input => input instanceof HTMLInputElement);

    if (matchingInputs.length === 0) {
      // If no inputs match, try selecting the first available option in each fieldset
      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
      if (fieldsets.length === 0) return;

      const selectedInputs = [];
      for (const fieldset of fieldsets) {
        const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? [])
          .filter(radio => radio instanceof HTMLInputElement);
        const firstAvailable = radios.find(radio =>
          radio.dataset.optionAvailable !== 'false' &&
          !radio.disabled
        );
        if (firstAvailable) {
          selectedInputs.push(firstAvailable);
        } else {
          return;
        }
      }

      if (selectedInputs.length === fieldsets.length) {
        // Uncheck all inputs first
        fieldsets.forEach(fieldset => {
          const allInputs = fieldset.querySelectorAll('input[type="radio"]');
          allInputs.forEach(input => {
            if (input instanceof HTMLInputElement) {
              input.checked = false;
              input.dataset.currentChecked = 'false';
              input.dataset.previousChecked = 'false';
            }
          });
        });

        // Select the first available option in each fieldset
        selectedInputs.forEach(input => {
          if (input instanceof HTMLInputElement) {
            input.checked = true;
            this.updateSelectedOption(input);
          }
        });

        // Trigger variant change with the last input
        const lastInput = selectedInputs[selectedInputs.length - 1];
        if (lastInput && lastInput instanceof HTMLInputElement) {
          const selectedVariantId = lastInput.dataset.variantId;
          if (selectedVariantId) {
            this.#updateProductFormFromVariantId(selectedVariantId);
            this.#updateUrlWithVariant(selectedVariantId);
            const syntheticEvent = new Event('change', { bubbles: true });
            Object.defineProperty(syntheticEvent, 'target', { value: lastInput, enumerable: true, configurable: true });
            this.variantChanged(syntheticEvent);
          }
        }
      }
      return;
    }

    // If we found matching inputs, select the first one (they should all be for the same variant)
    const matchingInput = matchingInputs[0];
    if (matchingInput && matchingInput instanceof HTMLInputElement) {
      // Check if already selected
      if (matchingInput.checked) {
        this.#updateProductFormFromVariantId(variantId);
        return;
      }

      // Uncheck all inputs in the same fieldset first
      const fieldset = matchingInput.closest('fieldset');
      if (fieldset) {
        const allInputs = fieldset.querySelectorAll('input[type="radio"]');
        allInputs.forEach(input => {
          input.checked = false;
          input.dataset.currentChecked = 'false';
          input.dataset.previousChecked = 'false';
        });
      }

      // Select the matching input
      matchingInput.checked = true;
      this.updateSelectedOption(matchingInput);

      // Update the product form with the variant ID
      this.#updateProductFormFromVariantId(variantId);
      this.#updateUrlWithVariant(variantId);

      // Create a synthetic event to trigger variant change
      const syntheticEvent = new Event('change', { bubbles: true });
      Object.defineProperty(syntheticEvent, 'target', { value: matchingInput, enumerable: true, configurable: true });

      // Trigger the variant change flow
      this.variantChanged(syntheticEvent);
    }
  }

  /**
   * Selects a variant based on the URL parameter if present
   */
  #selectVariantFromUrl() {
    // Only auto-select if we're on a product page
    const isOnProductPage = this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isOnProductPage) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get('variant');

    if (!variantIdFromUrl) {
      return;
    }

    // Find the input/option that matches this variant ID
    const matchingInput = this.querySelector(`input[data-variant-id="${variantIdFromUrl}"]`);
    const matchingOption = this.querySelector(`select option[data-variant-id="${variantIdFromUrl}"]`);

    if (matchingInput && matchingInput instanceof HTMLInputElement) {
      // Check if this input is already selected
      if (matchingInput.checked) {
        // Still update the form to ensure button is enabled
        this.#updateProductFormFromVariantId(variantIdFromUrl);
        return;
      }
      // Uncheck all inputs in the same fieldset first
      const fieldset = matchingInput.closest('fieldset');
      if (fieldset) {
        const allInputs = fieldset.querySelectorAll('input[type="radio"]');
        allInputs.forEach(input => {
          input.checked = false;
          input.dataset.currentChecked = 'false';
          input.dataset.previousChecked = 'false';
        });
      }

      // Select the matching input
      matchingInput.checked = true;
      this.updateSelectedOption(matchingInput);

      // Immediately update the product form with the variant ID
      this.#updateProductFormFromVariantId(variantIdFromUrl);

      // Trigger the variant change flow via native bubbling event
      // so `event.target` is set by the browser.
      matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (matchingOption && matchingOption instanceof HTMLOptionElement) {
      const selectElement = matchingOption.closest('select');
      if (selectElement) {
        // Check if this option is already selected
        if (selectElement.value === matchingOption.value) {
          // Still update the form to ensure button is enabled
          this.#updateProductFormFromVariantId(variantIdFromUrl);
          return;
        }
        selectElement.value = matchingOption.value;
        this.updateSelectedOption(selectElement);

        // Immediately update the product form with the variant ID
        this.#updateProductFormFromVariantId(variantIdFromUrl);

        // Trigger the variant change flow via native bubbling event
        // so `event.target` is set by the browser.
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      console.warn('[VariantPicker] Could not find matching variant input/option for variant ID:', variantIdFromUrl);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    if (this.#deferredVariantChangeTimeout !== undefined) {
      clearTimeout(this.#deferredVariantChangeTimeout);
      this.#deferredVariantChangeTimeout = undefined;
    }
  }

  /**
   * Handles the variant change event.
   * Lets the browser paint the native :checked selection first, then defers
   * data-attr / pill / legend / fetch work so presentation delay stays low.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const target = event.target;
    const selectedOption =
      target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;

    if (!selectedOption) return;

    // Store the selected option value ID before morphing, so we can restore it after
    const selectedOptionValueId = selectedOption.dataset.optionValueId;

    // Do not call updateSelectedOption here.
    // The browser already applied :checked from the click; writing data-current-checked /
    // data-previous-checked in this turn forces style recalc and increases presentation delay.
    // Capture values needed after yield — event target refs can go stale on rapid clicks
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;
    const variantId = selectedOption.dataset.variantId || null;
    const isOnProductPage = this.dataset.templateProductMatch === 'true' && !target.closest('product-card') && !target.closest('quick-add-dialog');
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
    const isOnProductCard = Boolean(target.closest('product-card'));

    if (this.#deferredVariantChangeTimeout !== undefined) {
      clearTimeout(this.#deferredVariantChangeTimeout);
    }

    this.#deferredVariantChangeTimeout = setTimeout(() => {
      this.#deferredVariantChangeTimeout = undefined;

      // After first paint: pill animation attrs, legend text, layout measurement
      this.updateSelectedOption(target);

      this.#processVariantChange({
        selectedOption,
        selectedOptionValueId,
        connectedProductUrl,
        variantId,
        isOnProductPage,
        isOnFeaturedProductSection,
        isOnProductCard,
      });
    }, 0);
  }

  /**
   * Deferred variant-change work: events, section fetch, and URL updates.
   * @param {object} params
   * @param {HTMLElement} params.selectedOption
   * @param {string | undefined} params.selectedOptionValueId
   * @param {string | undefined} params.connectedProductUrl
   * @param {string | null} params.variantId
   * @param {boolean} params.isOnProductPage
   * @param {boolean} params.isOnFeaturedProductSection
   * @param {boolean} params.isOnProductCard
   */
  #processVariantChange({
    selectedOption,
    selectedOptionValueId,
    connectedProductUrl,
    variantId,
    isOnProductPage,
    isOnFeaturedProductSection,
    isOnProductCard,
  }) {
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOptionValueId ?? '' }));

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const morphElementSelector = loadsNewProduct ? 'main' : isOnFeaturedProductSection ? 'featured-product-information' : undefined;

    const requestUrl = this.buildRequestUrl(selectedOption);

    // Store the selected option value ID to restore after morph
    this.#pendingSelectedOptionValueId = selectedOptionValueId;
    this.fetchUpdatedSection(requestUrl, morphElementSelector);

    const url = new URL(window.location.href);

    // Only update URL with variant ID if we're on the actual product page, not on product cards
    // Product cards should not have variant IDs in their URLs
    if (isOnProductPage && !isOnProductCard) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    } else {
      // For product cards, always remove variant from URL
      url.searchParams.delete('variant');
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct && newUrl) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the "Size: 34 / 2XL" (or Color: …) selected-value text in the fieldset legend.
   * @param {number} fieldsetIndex
   */
  #updateSelectedValueLabel(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];
    const currentIndex = checkedIndices?.[0];

    if (!fieldset || !radios || currentIndex === undefined || !radios[currentIndex]) return;

    const legend = fieldset.querySelector('legend');
    const selectedValueSpan = legend?.querySelector('.variant-option__selected-value');
    if (!selectedValueSpan) return;

    const label = radios[currentIndex].closest('label');
    const valueText =
      label?.querySelector('.variant-option__button-label__text')?.textContent?.trim() ||
      radios[currentIndex].value;
    selectedValueSpan.textContent = valueText;
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   * @param {{ measureLayout?: boolean, updateLegend?: boolean }} [options]
   *   measureLayout: when false, skip offsetWidth reads (defer for INP).
   *   updateLegend: when false, skip legend selected-value text (defer for INP).
   */
  updateSelectedOption(target, options = {}) {
    const { measureLayout = true, updateLegend = true } = options;

    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        // Ensure we always have an array to work with, even if nothing was preselected
        const checkedIndices = this.#checkedIndices[fieldsetIndex] || (this.#checkedIndices[fieldsetIndex] = []);
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          if (measureLayout) {
            this.updateFieldsetCss(fieldsetIndex);
          }

          if (updateLegend) {
            this.#updateSelectedValueLabel(fieldsetIndex);
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // After morphing, restore the selected option if we have a pending selection
          if (this.#pendingSelectedOptionValueId) {
            const inputToSelect = this.querySelector(`input[data-option-value-id="${this.#pendingSelectedOptionValueId}"]`);
            if (inputToSelect) {
              inputToSelect.checked = true;
              this.updateSelectedOption(inputToSelect);
            } else {
              console.warn('[VariantPicker] Could not find input to restore:', this.#pendingSelectedOptionValueId);
            }
          }

          // We grab the variant object from the response and dispatch an event with it.
          const selectedOptionId = this.selectedOptionId;

          if (selectedOptionId) {
            const variantData = JSON.parse(textContent);
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
            // Clear the pending selection after successful dispatch
            this.#pendingSelectedOptionValueId = undefined;
          } else {
            // Try using the pending selection ID if available
            if (this.#pendingSelectedOptionValueId) {
              const variantData = JSON.parse(textContent);
              this.dispatchEvent(
                new VariantUpdateEvent(variantData, this.#pendingSelectedOptionValueId, {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                })
              );
              this.#pendingSelectedOptionValueId = undefined;
            }
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

